// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { bool, number, func, shape, array, object, string } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { uniqueId } from 'lodash';
import { Info, AlertTriangle, ArrowUpNarrowWide, ArrowDownWideNarrow, ArrowDownUp, ClockAlert } from 'lucide-react';
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  DataTableSkeleton,
} from '@carbon/react';

import useNumberUtils from 'Hooks/useNumberUtils';
import ReportsPagination from 'Containers/Reports/ReportsPagination';
import FilterReports from 'Containers/Reports/FilterReports';

import IconWarranty from 'Assets/images/warranty.svg';
import IconInvoice from 'Assets/images/pdf.svg';
import IconXls from 'Assets/images/xls.svg';
import IconCsv from 'Assets/images/csv.svg';

const StyledInvoices = styled.div`
  .invoiceNumber {
    display: flex;
    align-items: center;

    &.noOverdue {
      margin-left: 36px;
    }

    svg {
      margin-right: 12px;
    }
  }

  .paidStatusUnknown {
    p {
      font-size: 12px;
    }
  }

  .partiallyPaidTooltip {
    padding: 0;

    .partiallyPaidTooltipContent {
      display: flex;
      flex-direction: column;

      .remainingAmount {
        background: #FCF3F1;
        padding: 15px;
        display: flex;
        justify-content: space-between;

        .value {
          font-weight: 600;
        }
      }

      .partiallyPaidInfo {
        padding: 15px;
        color: #999999;
        display: flex;
        align-items: flex-start;
        gap: 5px;
      }
    }
  }

  [data-label='isPaid'] {
    font-weight: 600;

    .unpaid {
      color: #d52b1e;
    }
  
    .paid {
      color: #78b075;
    }

    .partiallyPaid {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 5px;
      color: #E8720B;
    }
  }

  .actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 5px;

    button:not(.cds--btn) {
      padding: 7px 3px;
      border: none;
      background: transparent;

      &.fileType {
        cursor: pointer;
        transform: none;
        min-width: 30px;
        min-height: 31px;

        svg {
          width: 24px;
          height: 30px;
        }
      }
    }
  }

  .cds--data-table {
    font-size: 16px;
    border: 1px solid #e2e2e2;

    thead th {
      color: #3a3a3a;
      font-size: 16px;
      font-weight: 600;
      background: #f5f7f9;

      &:first-of-type {
        .cds--table-header-label {
          text-align: left;
          
          button {
            justify-content: start;
          }
        }
      }

      &:last-of-type {
        .cds--table-header-label {
          text-align: right;
          
          button {
            justify-content: end;
          }
        }
      }

      .cds--table-header-label {
        font-size: 16px;
        font-weight: 500;
        text-align: center;

        button {
          width: 100%;
          cursor: pointer;
          display: flex;
          justify-content: center;
          align-items: center;
          background: transparent;
          border: none;
          gap: 5px;
          font-size: inherit;
          font-weight: inherit;


          svg {
            width: 18px;
            height: 18px;
            stroke: #707070;
            min-width: 18px;
          }
        }
      }
    }

    tbody tr td {
      background-color: #fff;
      text-align: center;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .cds--data-table {
      border: none;

      > thead {
        display: none;
      }

      > tbody {
        background: #fff;

        tr {
          display: flex;
          flex-direction: column;
          height: auto;
          margin-top: 20px;
          border: 1px solid #e2e2e2;
          position: relative;

          td {
            border: transparent;
            text-align: left;

            &:first-of-type {
            background: #F5F7F9;
            position: relative;

            .actions button:not(.cds--btn) {
              padding: 0;
            }

            .invoiceNumber.noOverdue {
              margin-left: 0;
            }

            .overdueTooltip {
              position: absolute;
              right: 0;
              top: 8px;
            }
          }

          &:last-of-type {
            height: 50px;
          }
          }
        }
      }
    }
  }
`;

const StyledTableCell = styled(TableCell)`
  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    &[data-label="invoiceNumber"],
    &[data-label="purchaseOrder"],
    &[data-label="date"],
    &[data-label="netValue"],
    &[data-label="totalWithVat"],
    &[data-label="isPaid"],  
    &[data-label="actions"] {    
      display: flex;
      align-items: center;
      height: 40px;
      padding-left: 175px;

      &:before {
        content: "${ props => props.name }:";
        position: absolute;
        left: 10px;
        width: 170px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 600;
      }
    } 
  }
`;

const InvoicesComponent = ({
  invoices,
  loading,
  pagination,
  getInvoices,
  handleMobileOrderChange,
  currentSort,
  defaultSortId,
  sortOptions,
  onDownload,
  isExternalReseller,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatDate, formatNumber } = useNumberUtils();

  const mapHeaderToLabel = label => {
    let mappedLabel = label;
    if (label === 'totalWithVat') {
      mappedLabel = 'valueWithVat';
    }
    if (label === 'actions') {
      mappedLabel = 'downloads';
    }
    return t(`pages.${mappedLabel}`);
  };

  const headerData = useMemo(
    () => [
      {
        key: 'invoiceNumber',
        header: t('pages.invoiceNumber'),
      },
      {
        key: 'purchaseOrder',
        header: t('pages.purchaseOrder'),
      },
      {
        key: 'date',
        header: t('pages.date'),
      },
      {
        key: 'netValue',
        header: t('pages.netValue'),
      },
      {
        key: 'totalWithVat',
        header: t('pages.valueWithVat'),
      },
      {
        key: 'isPaid',
        header: t('pages.isPaid'),
      },
      {
        key: 'actions',
        header: t('pages.actions'),
      },
    ],
    []
  );

  const renderTableHeader = header => {
    const mappedSortingParams = {
      invoiceNumber: 'invoice_number',
      purchaseOrder: 'purchase_order',
      date: 'issue_date',
      netValue: 'net_value',
      totalWithVat: 'net_value',
      isPaid: 'is_paid',
    };

    const [orderBy, orderDirection] = currentSort.id.split('-');
    const isSorted = orderBy === mappedSortingParams[header.key];
    const isAsc = orderDirection === 'asc';

    let sortIcon = <ArrowDownUp />;
    let sortDirection = 'asc';

    if (isSorted && currentSort.id !== defaultSortId) {
      sortIcon = isAsc ? <ArrowUpNarrowWide /> : <ArrowDownWideNarrow />;
      sortDirection = isAsc ? 'desc' : 'none';
    }

    if (isSorted && currentSort.id === defaultSortId) {
      sortIcon = <ArrowDownWideNarrow />;
      sortDirection = 'asc';
    }

    return (header.key === 'actions') ? <span>{ header.header }</span> : (
      <button type="button" onClick={ () => handleMobileOrderChange(`${ mappedSortingParams[header.key] }-${ sortDirection }`) }>
        <span>{ header.header }</span>
        { sortIcon }
      </button>
    );
  };

  const renderCell = cell => {
    if (cell.info.header === 'invoiceNumber') {
      const uid = uniqueId();
      const { number: invoiceNumber, overdue } = cell.value;

      return cell.value !== 0 && (
        <div className={ `invoiceNumber ${ overdue ? '' : 'noOverdue' }` }>
          { overdue && <span className="overdueTooltip" id={ `tooltip-${ uid }}` }><ClockAlert color="#D52B1E" /></span> }
          <ReactTooltip
            className="tooltip"
            anchorId={ `tooltip-${ uid }}` }
            variant="light"
            offset={ 10 }
            content={ (
              <p>{ t('profile.overdue') }</p>
            ) }
          />
          <span>{ invoiceNumber }</span>
        </div>
      );
    }

    if (cell.info.header === 'date') {
      return formatDate(cell.value, { timeStyle: undefined });
    }

    if (cell.info.header === 'netValue' || cell.info.header === 'totalWithVat') {
      const { value, currency } = cell.value;
      return formatNumber(value, { currency });
    }

    if (cell.info.header === 'isPaid') {
      const { paid, paidAmount, remainingAmount, currency } = cell.value;
      const uid = uniqueId();

      if (paid === false && paidAmount !== 0) {
        return (
          <>
            <span className="partiallyPaid" id={ `tooltip-${ uid }}` }>{ t('orders.partiallyPaid') }<Info size={ 18 } /></span>
            <ReactTooltip
              className="partiallyPaidTooltip opaque"
              anchorId={ `tooltip-${ uid }}` }
              variant="light"
              offset={ 10 }
              content={ (
                <div className="partiallyPaidTooltipContent">
                  <div className="remainingAmount">
                    <span>{ t('orders.remainingAmount') }:</span>
                    <span className="value">{ formatNumber(remainingAmount, { currency }) }</span>
                  </div>
                  <div className="partiallyPaidInfo">
                    <AlertTriangle />
                    <span dangerouslySetInnerHTML={ { __html: t('orders.partiallyPaidInfo') } } />
                  </div>
                </div>
              ) }
            />
          </>
        );
      }

      if (paid === false) {
        return <span className="unpaid">{ t('orders.unpaid') }</span>;
      }

      if (paid === true) {
        return <span className="paid">{ t('orders.paid') }</span>;
      }

      return (
        <>
          <span id={ `tooltip-${ uid }}` }><Info size={ 24 } /></span>
          <ReactTooltip
            className="paidStatusUnknown"
            anchorId={ `tooltip-${ uid }}` }
            variant="light"
            offset={ 10 }
            content={ (
              <p>{ t('orders.paidStatusUnknown') }</p>
            ) }
          />
        </>
      );
    }

    if (cell.info.header === 'actions') {
      const { id } = cell.value;

      return (
        <div className="actions">
          <div className="downloads">
            { !isExternalReseller && (
              <button
                type="button"
                className="download fileType"
                onClick={ () => onDownload('warranty', id) }
              >
                <IconWarranty />
              </button>
            ) }
            <button
              type="button"
              className="download fileType"
              onClick={ () => onDownload('csv', id) }
            >
              <IconCsv />
            </button>
            <button
              type="button"
              className="download fileType"
              onClick={ () => onDownload('xlsx', id) }
            >
              <IconXls />
            </button>
            <button
              type="button"
              className="download fileType"
              onClick={ () => onDownload('pdf', id) }
            >
              <IconInvoice />
            </button>
          </div>
        </div>
      );
    }

    return cell.value;
  };

  return (
    <StyledInvoices theme={ theme }>
      <FilterReports
        sortOptions={ sortOptions }
        currentSort={ currentSort }
        handleMobileOrderChange={ handleMobileOrderChange }
        getTableData={ getInvoices }
      />
      { loading ? (
        <DataTableSkeleton showToolbar={ false } showHeader={ false } />
      ) : (
        <DataTable rows={ invoices } headers={ isExternalReseller ? headerData.filter((_, index) => index !== 4) : headerData }>
          {({ rows, headers, getHeaderProps, getTableProps }) => (
            <TableContainer>
              <Table { ...getTableProps() }>
                <TableHead>
                  <TableRow>
                    {headers.map(header => (
                      <TableHeader
                        { ...getHeaderProps({ header }) }
                      >
                        { renderTableHeader(header) }
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  { invoices.length === 0 && (<TableRow><TableCell colSpan={ headers.length + 1 }>{ t('orders.noResultsFound') }</TableCell></TableRow>) }
                  { rows.map(row => (
                    <TableRow key={ row.id }>
                      { row.cells.map(cell => (
                        <StyledTableCell
                          data-label={ cell.info.header }
                          name={ () => mapHeaderToLabel(cell.info.header) }
                          key={ cell.id }
                        >
                          { renderCell(cell) }
                        </StyledTableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      )}
      <ReportsPagination loading={ loading } { ...pagination } />
    </StyledInvoices>
  );
};

InvoicesComponent.propTypes = {
  invoices: array,
  loading: bool,
  pagination: shape({
    currentPage: number,
    recordsPerPage: number,
    totalPages: number,
    totalRecords: number,
    loading: bool,
  }),
  getInvoices: func,
  handleMobileOrderChange: func,
  currentSort: object,
  defaultSortId: string,
  sortOptions: array,
  onDownload: func,
  isExternalReseller: bool,
};

InvoicesComponent.displayName = 'InvoicesComponent';

export default InvoicesComponent;
